import React, { useEffect, useState } from "react";
import * as styles from "./DesktopHeader.module.scss";
import { Link, TypographyV2, NavigationButton } from "@components";
import { useOnScroll } from "@helpers/Hooks";
import * as fadeStyles from "../../BackgroundFadeContainer/BackgroundFadeContainer.module.scss";
import { RequestDemoButtonV2 } from "@components/Button_new/Button";
import { StaticImage } from "gatsby-plugin-image";
import { LINKS, LinkType } from "@components/Link/Link";
import classNames from "classnames";
import Products from "../HeaderLinks/Products";
import Solutions from "../HeaderLinks/Solutions";
import Resources from "../HeaderLinks/Resources";
import Company from "../HeaderLinks/Company";
import { navigate } from "gatsby";

const LOGO_WIDTH = 124;

export type Theme = "dark" | "light" | "purple";

type Props = {
	defaultHeader?: Theme;
	fixedColor?: boolean;
};

type Link = {
	id: number;
	title: string;
	content?: JSX.Element;
	to?: LinkType;
};

const links: Link[] = [
	{
		id: 1,
		title: "Products",
		content: <Products />,
	},
	{
		id: 2,
		title: "Solutions",
		content: <Solutions />,
	},
	{
		id: 3,
		title: "Resources",
		content: <Resources />,
	},
	{
		id: 4,
		title: "Pricing",
		to: "PRICING",
	},
	{
		id: 5,
		title: "Company",
		content: <Company />,
	},
];
export default function DesktopHeader({}: Props) {
	const [scrolled, setScrolled] = useState(false);
	const [selectedLink, setSelectedLink] = useState<null | Link>(null);

	const updateScroll = () => {
		const SCROLLED_OFFSET = 10;
		const hasScrolled = window.scrollY > SCROLLED_OFFSET;
		if (hasScrolled) {
			setScrolled(hasScrolled);
		} else {
			setScrolled(false);
		}
	};

	useEffect(() => {
		if (!selectedLink) return;
		const handleSelectedLink = (e: MouseEvent) => {
			const menu = document.querySelector("#desktop-menu");
			if (menu?.contains(e.target as Node) || e.target === menu) return;
			setSelectedLink(null);
		};
		const handleKeydown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setSelectedLink(null);
			}
		};

		document.addEventListener("click", handleSelectedLink);
		document.addEventListener("keydown", handleKeydown);
		return () => {
			document.removeEventListener("click", handleSelectedLink);
			document.removeEventListener("keydown", handleKeydown);
		};
	}, [selectedLink]);

	useEffect(() => {
		if (window.scrollY > 0) {
			setScrolled(true);
		}
	}, []);

	useOnScroll(updateScroll);

	return (
		<>
			<div
				className={classNames(styles.fixedHeaderContainer, {
					[styles.scrolled]: scrolled || !!selectedLink,
				})}
			>
				<div className={styles.fixedHeader}>
					<Link to={LINKS["LANDING"].to}>
						<StaticImage
							loading="eager"
							src="../img/Logo.png"
							alt="Genemod logo"
							width={LOGO_WIDTH}
							style={{
								marginBottom: "-2px",
							}}
						/>
					</Link>

					<div>
						<div className={styles.links}>
							{links.map((link, index) => {
								const { title, to } = link;
								return (
									<NavigationButton
										key={index}
										title={title}
										open={selectedLink?.id === link.id}
										onClick={() => {
											if (to) {
												navigate(LINKS[to].to);
											} else if (
												selectedLink?.id === link.id
											) {
												setSelectedLink(null);
											} else {
												setSelectedLink(link);
											}
										}}
										to={to}
									/>
								);
							})}
							<div className={styles.actions}>
							<Link
								link="LOGIN"
								className={styles.linkButtonContainer}
							>
								<TypographyV2
									variant="BODY_TEXT_MEDIUM_V1"
									weight="MEDIUM"
									color="menu-item"
									className={styles.login}
								>
									{LINKS.LOGIN.title}
								</TypographyV2>
							</Link>
							<RequestDemoButtonV2 variant="v2" />
						</div>
						</div>
						
					</div>
				</div>
			</div>
			{selectedLink && (
				<div className={styles.contextualMenuContainer}>
					<div className={styles.contextualMenu} id="desktop-menu">
						{selectedLink.content}
					</div>
				</div>
			)}
		</>
	);
}

export const useTheme = (defaultHeader: Theme) => {
	const [theme, setTheme] = useState<Theme | undefined>(
		getHeaderTheme(defaultHeader)
	);

	const newTheme = getHeaderTheme(defaultHeader);
	if (theme !== newTheme) {
		setTheme(newTheme);
	}

	useOnScroll(() => setTheme(getHeaderTheme(defaultHeader)));

	return theme;
};

const getHeaderTheme = (defaultHeader: Theme): Theme | undefined => {
	if (typeof document !== "undefined") {
		// get all elements at the top center of the screen
		const elementsUnderHeader = document.elementsFromPoint(
			window.innerWidth / 2,
			5
		);

		// look through all the overlapping elements, finding any BackgroundFadeContainer elements
		// with a dark background
		const isOverDarkBackground = !!elementsUnderHeader.some(
			(e) =>
				e?.className?.includes?.(fadeStyles.blueFade) ||
				e?.className?.includes?.(fadeStyles.solidBlue)
		);
		const isOverPurpleBackground = !!elementsUnderHeader.some((e) =>
			e?.className?.includes?.(fadeStyles.purpleBlueFade)
		);
		if (isOverDarkBackground) return "dark";
		else if (isOverPurpleBackground) return "purple";
	}
	return defaultHeader;
};
